/**
 * Design-system primitives for the signed-in app.
 *
 * Tokens live in src/app/styles.css (@theme: ink / accent / action brand
 * colors, semantic success / warn / danger / info, shadows, radii) and the
 * `.cmcp-*` component classes below them. These components are thin,
 * typed wrappers so pages compose the same buttons, badges, cards, stats,
 * toolbars, tables and loading skeletons everywhere.
 *
 *   import { Button, Badge, Card, StatCard, Toolbar, Skeleton } from "../components/ui";
 *   import { PageHeader, EmptyState } from "../components/PageHeader";
 */
import type { ButtonHTMLAttributes, ReactNode, HTMLAttributes, TableHTMLAttributes } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

export { PageHeader, EmptyState } from "./PageHeader";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_CLASS: Record<ButtonVariant, string> = {
  primary: "cmcp-btn-primary",
  secondary: "cmcp-btn",
  ghost: "cmcp-btn-ghost",
  danger: "cmcp-btn-danger",
};

/** Class names for a button-styled element (for <Link>/<a> as buttons). */
export function buttonClass(variant: ButtonVariant = "secondary", size: "md" | "sm" = "md", extra = ""): string {
  return [BUTTON_CLASS[variant], size === "sm" ? "cmcp-btn-sm" : "", extra].filter(Boolean).join(" ");
}

export function Button({ variant = "secondary", size = "md", icon: Icon, className = "", children, type = "button", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant; size?: "md" | "sm"; icon?: LucideIcon;
}) {
  return (
    <button type={type} className={buttonClass(variant, size, className)} {...props}>
      {Icon && <Icon size={size === "sm" ? 13 : 15} aria-hidden="true" />}
      {children}
    </button>
  );
}

/** A router link styled as a button. */
export function ButtonLink({ to, variant = "secondary", size = "md", icon: Icon, className = "", children }: {
  to: string; variant?: ButtonVariant; size?: "md" | "sm"; icon?: LucideIcon; className?: string; children: ReactNode;
}) {
  return (
    <Link to={to} className={buttonClass(variant, size, className)}>
      {Icon && <Icon size={size === "sm" ? 13 : 15} aria-hidden="true" />}
      {children}
    </Link>
  );
}

export type BadgeTone = "neutral" | "brand" | "success" | "warn" | "danger" | "info" | "outline";

export function Badge({ tone = "neutral", icon: Icon, className = "", children, title }: {
  tone?: BadgeTone; icon?: LucideIcon; className?: string; children: ReactNode; title?: string;
}) {
  return (
    <span className={`cmcp-badge ${className}`} data-tone={tone} title={title}>
      {Icon && <Icon size={11} aria-hidden="true" />}
      {children}
    </span>
  );
}

export function Card({ title, description, actions, children, className = "", bodyClassName = "", ...props }: Omit<HTMLAttributes<HTMLElement>, "title"> & {
  title?: ReactNode; description?: ReactNode; actions?: ReactNode; bodyClassName?: string;
}) {
  return (
    <section className={`cmcp-card ${className}`} {...props}>
      {(title || actions) && (
        <div className="cmcp-card-head">
          <div className="min-w-0">
            {title && <h2 className="cmcp-card-title">{title}</h2>}
            {description && <p className="cmcp-section-desc">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={bodyClassName || "cmcp-card-body"}>{children}</div>
    </section>
  );
}

export function StatCard({ label, value, hint, icon: Icon, to }: {
  label: ReactNode; value: ReactNode; hint?: ReactNode; icon?: LucideIcon; to?: string;
}) {
  const body = (
    <>
      <div className="cmcp-stat-label">{Icon && <Icon size={13} aria-hidden="true" />}{label}</div>
      <div className="cmcp-stat-value">{value}</div>
      {hint && <div className="cmcp-stat-hint">{hint}</div>}
    </>
  );
  return to ? <Link to={to} className="cmcp-stat">{body}</Link> : <div className="cmcp-stat">{body}</div>;
}

/** Filters / search row above a list or table. */
export function Toolbar({ children, className = "", label }: { children: ReactNode; className?: string; label?: string }) {
  return <div className={`cmcp-toolbar ${className}`} role={label ? "group" : undefined} aria-label={label}>{children}</div>;
}

export function Table({ children, className = "", ...props }: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="cmcp-table-wrap">
      <table className={`cmcp-table ${className}`} {...props}>{children}</table>
    </div>
  );
}

export function Skeleton({ className = "h-4 w-full" }: { className?: string }) {
  return <span aria-hidden="true" className={`cmcp-skeleton ${className}`} />;
}

/** Loading placeholder for lists and tables, announced once to assistive tech. */
export function SkeletonRows({ rows = 5, label = "Loading…" }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-live="polite" className="space-y-2.5">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="cmcp-card flex items-center gap-3 p-3">
          <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className={`h-3.5 ${index % 2 ? "w-2/5" : "w-3/5"}`} />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Loading placeholder for card grids. */
export function SkeletonCards({ count = 6, label = "Loading…" }: { count?: number; label?: string }) {
  return (
    <div role="status" aria-live="polite" className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      <span className="sr-only">{label}</span>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="cmcp-card space-y-2 p-4">
          <Skeleton className="h-3 w-1/4" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

/** Section heading inside a page (below the PageHeader). */
export function SectionHeading({ title, description, actions, id }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; id?: string }) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 id={id} className="cmcp-section-title">{title}</h2>
        {description && <p className="cmcp-section-desc">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
